import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUserRole } from "../../../store/authSlice";
import { baListAccounts, baCreateAccount, baExportLedger } from "../../../services/bankAccounts";

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });
// Balances read the way our own books (Tally) show a bank account: money in
// the bank = Dr (stored positive), overdrawn = Cr (stored negative).
const fmtBalance = (n) => {
  const v = Number(n) || 0;
  return `${fmtMoney(Math.abs(v))} ${v < 0 ? "Cr" : "Dr"}`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "medium" }) : "—");

const Accounts = () => {
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newOpening, setNewOpening] = useState("0");
  const [newOpeningType, setNewOpeningType] = useState("debit"); // "debit" (Dr, money in bank) | "credit" (Cr, overdrawn)
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    baListAccounts()
      .then((r) => setAccounts(r.data?.accounts || []))
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleSelected = (id) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);

  const addAccount = async () => {
    if (!newName.trim()) {
      Swal.fire({ icon: "warning", title: "Account name is required." });
      return;
    }
    setSaving(true);
    try {
      const amt = Math.abs(Number(newOpening) || 0);
      const openingBalance = newOpeningType === "credit" ? -amt : amt;
      await baCreateAccount({ name: newName.trim(), openingBalance });
      Swal.fire({ icon: "success", title: "Account created", timer: 1200, showConfirmButton: false });
      setShowAdd(false);
      setNewName("");
      setNewOpening("0");
      setNewOpeningType("debit");
      load();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not create account",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  const exportSelected = async () => {
    if (!selectedIds.length) {
      Swal.fire({ icon: "warning", title: "Select at least one account to export." });
      return;
    }
    setExporting(true);
    try {
      await baExportLedger(selectedIds);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Export failed" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Netplus Banks" motherMenu="Reports" />
      <div className="card">
        <div className="card-header d-flex flex-wrap gap-2 align-items-center">
          <h4 className="card-title mb-0 me-auto">Bank Accounts</h4>
          <button
            className="btn btn-outline-primary btn-sm"
            disabled={!selectedIds.length || exporting}
            onClick={exportSelected}
          >
            {exporting
              ? "Exporting…"
              : `Download Excel${selectedIds.length > 1 ? " (combined + cumulative)" : ""}`}
          </button>
          {isSuperAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd((v) => !v)}>
              + Add Account
            </button>
          )}
        </div>

        {showAdd && (
          <div className="card-body border-bottom bg-light">
            <div className="row g-2 align-items-end">
              <div className="col-sm-5">
                <label className="form-label small mb-1">Account name</label>
                <input
                  className="form-control form-control-sm"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Harveer Enterprises"
                />
              </div>
              <div className="col-sm-3">
                <label className="form-label small mb-1">Opening balance</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={newOpening}
                  onChange={(e) => setNewOpening(e.target.value)}
                />
              </div>
              <div className="col-sm-2">
                <label className="form-label small mb-1">Balance is</label>
                <select
                  className="form-select form-select-sm"
                  value={newOpeningType}
                  onChange={(e) => setNewOpeningType(e.target.value)}
                >
                  <option value="debit">Debit (Dr) — money in bank</option>
                  <option value="credit">Credit (Cr) — overdrawn</option>
                </select>
              </div>
              <div className="col-sm-4 d-flex gap-2">
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={addAccount}>
                  {saving ? "Saving…" : "Save"}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowAdd(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">Loading…</div>
          ) : accounts.length === 0 ? (
            <div className="text-muted text-center py-4">
              No bank accounts yet.{" "}
              {isSuperAdmin ? "Add one above to get started." : "Ask a super-admin to add one."}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th aria-label="Select" style={{ width: 36 }} />
                    <th>Account</th>
                    <th>Current Balance</th>
                    <th>Last Upload Through</th>
                    <th aria-label="Action" />
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a._id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={!!selected[a._id]}
                          onChange={() => toggleSelected(a._id)}
                        />
                      </td>
                      <td
                        className="fw-semibold text-primary"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/bank-accounts/${a._id}`)}
                      >
                        {a.name}
                        {a.notes ? <small className="d-block text-muted fw-normal">{a.notes}</small> : null}
                      </td>
                      <td className={a.currentBalance < 0 ? "text-danger fw-semibold" : "fw-semibold"}>
                        {fmtBalance(a.currentBalance)}
                      </td>
                      <td>{fmtDate(a.lastUploadClosingDate)}</td>
                      <td className="text-end">
                        <button
                          className="btn btn-xs btn-outline-primary"
                          onClick={() => navigate(`/bank-accounts/${a._id}`)}
                        >
                          Open Ledger
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Accounts;
